import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/lib/useBrand";
import { API } from "@/lib/api";
import { GoogleLogo, Lightning } from "@phosphor-icons/react";

export default function Login() {
  const { user, loading } = useAuth();
  const { name: appName, logoUrl, subtitle, initial } = useBrand();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, loading, navigate]);

  const handleGoogleLogin = () => {
    // Owned Google OAuth (de-tethered from auth.emergentagent.com). The backend
    // runs the authorization-code flow and sets the session cookie server-side,
    // then bounces back to /dashboard — no #session_id fragment exchange needed.
    window.location.href = `${API}/auth/google/start?redirect=/dashboard`;
  };

  return (
    <div className="min-h-screen bg-[#0A1628] text-[#F5F1E8] flex flex-col">
      <Navbar />
      <div className="flex-1 flex items-center justify-center px-6 py-16 relative">
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1602475063211-3d98d60e3b1f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbHV4dXJ5JTIwYWJzdHJhY3QlMjB0ZXh0dXJlJTIwZ3JhaW58ZW58MHx8fHwxNzgxNDUyODE3fDA&ixlib=rb-4.1.0&q=85)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative w-full max-w-md bg-[#0F1F38] border border-white/10 rounded-lg p-10 glow-amber"
          data-testid="login-card"
        >
          <div className="flex items-center gap-3 mb-8">
            {logoUrl ? (
              <img src={logoUrl} alt={appName} className="h-10 w-10 rounded-sm object-contain" />
            ) : (
              <span className="h-10 w-10 rounded-sm bg-gradient-to-br from-[#C9A961] to-[#E0B64B] flex items-center justify-center font-display font-black text-[#0A1628]">
                {initial}
              </span>
            )}
            <span className="flex flex-col leading-tight">
              <span className="font-wordmark font-semibold text-xl text-[#F5F1E8]">{appName}</span>
              {subtitle && (
                <span className="text-[10px] text-[#9AA5B5] tracking-wide">{subtitle}</span>
              )}
            </span>
          </div>

          <p className="text-sm tracking-[0.3em] uppercase font-bold text-[#C9A961] mb-4">
            Enter the forge
          </p>
          <h1 className="font-display text-3xl md:text-4xl font-light tracking-tight leading-tight mb-3">
            Sign in to start{" "}
            <span className="font-black">forging offers</span>.
          </h1>
          <p className="text-sm text-[#9AA5B5] mb-10 font-body leading-relaxed">
            Your generated Irresistible Offers are saved to a private vault tied
            to your Google account.
          </p>

          <Button
            onClick={handleGoogleLogin}
            className="w-full bg-white text-black hover:bg-[#f5f5f5] rounded-sm py-6 font-bold tracking-wide flex items-center justify-center gap-3"
            data-testid="google-login-btn"
          >
            <GoogleLogo weight="bold" className="h-5 w-5" />
            Continue with Google
          </Button>

          <div className="mt-8 pt-8 border-t border-white/5 flex items-center gap-3 text-xs text-[#808A9B]">
            <Lightning weight="fill" className="h-4 w-4 text-[#C9A961]" />
            <span>One click. No passwords. No friction.</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
